class C1FeatureTabs extends HTMLElement {
  connectedCallback() {
    if (this.isInitialized) return;

    this.tabs = Array.from(this.querySelectorAll('[data-feature-tab]'));
    this.items = Array.from(this.querySelectorAll('[data-feature-item]'));
    this.images = Array.from(this.querySelectorAll('[data-feature-image]'));
    this.panels = Array.from(this.querySelectorAll('[data-feature-panel]'));

    if (!this.tabs.length) return;

    this.tabs.forEach((tab, index) => {
      tab.addEventListener('mouseenter', () => this.activate(index));
      tab.addEventListener('focus', () => this.activate(index));
      tab.addEventListener('click', () => this.activate(index));
      tab.addEventListener('keydown', (event) => this.handleKeydown(event, index));
    });

    this.addEventListener('shopify:block:select', (event) => {
      const item = event.target.closest('[data-feature-item]');
      if (!item || !this.contains(item)) return;

      this.activate(Number(item.dataset.featureItem));
    });

    this.setupImagePreloading();
    this.activate(0);
    this.isInitialized = true;
  }

  setupImagePreloading() {
    const deferredImages = this.images
      .slice(1)
      .map((panel) => panel.querySelector('img'))
      .filter(Boolean);

    if (!deferredImages.length) return;

    const preload = () => deferredImages.forEach(preloadResponsiveImage);

    if (!('IntersectionObserver' in window)) {
      preload();
      return;
    }

    this.imagePreloadObserver = new IntersectionObserver(
      (entries, observer) => {
        if (!entries.some((entry) => entry.isIntersecting)) return;

        preload();
        observer.disconnect();
      },
      { rootMargin: '600px 0px' },
    );
    this.imagePreloadObserver.observe(this);
  }

  disconnectedCallback() {
    this.imagePreloadObserver?.disconnect();
  }

  activate(index, moveFocus = false) {
    if (index < 0 || index >= this.tabs.length) return;

    this.tabs.forEach((tab, tabIndex) => {
      const isActive = tabIndex === index;
      tab.setAttribute('aria-selected', String(isActive));
      tab.tabIndex = isActive ? 0 : -1;
      this.items[tabIndex]?.classList.toggle('is-active', isActive);

      if (this.images[tabIndex]) {
        this.images[tabIndex].hidden = !isActive;
        this.images[tabIndex].classList.toggle('is-active', isActive);
      }

      if (this.panels[tabIndex]) {
        this.panels[tabIndex].hidden = !isActive;
      }
    });

    if (moveFocus) this.tabs[index].focus();
  }

  handleKeydown(event, index) {
    let nextIndex = index;

    switch (event.key) {
      case 'ArrowDown':
      case 'ArrowRight':
        nextIndex = (index + 1) % this.tabs.length;
        break;
      case 'ArrowUp':
      case 'ArrowLeft':
        nextIndex = (index - 1 + this.tabs.length) % this.tabs.length;
        break;
      case 'Home':
        nextIndex = 0;
        break;
      case 'End':
        nextIndex = this.tabs.length - 1;
        break;
      default:
        return;
    }

    event.preventDefault();
    this.activate(nextIndex, true);
  }
}

function preloadResponsiveImage(image) {
  if (!image || image.dataset.c1Preloaded === 'true') return;

  image.dataset.c1Preloaded = 'true';
  image.loading = 'eager';
  image.fetchPriority = 'low';

  if (image.dataset.srcset) {
    image.srcset = image.dataset.srcset;

    if (image.dataset.sizes && image.dataset.sizes !== 'auto') {
      image.sizes = image.dataset.sizes;
    } else {
      const renderedWidth = Math.ceil(image.getBoundingClientRect().width);
      if (renderedWidth > 0) image.sizes = `${renderedWidth}px`;
    }
  }

  if (image.dataset.src) image.src = image.dataset.src;

  image.classList.remove('lazyload');
}

function setupColorwaysImagePreloading(scope = document) {
  const sectionSelector = "[id$='__section_explore-the-colorways_pc']";
  const section = scope.matches?.(sectionSelector)
    ? scope
    : scope.querySelector?.(sectionSelector);
  if (!section || section.dataset.c1ImagePreloadReady === 'true') return;

  section.dataset.c1ImagePreloadReady = 'true';

  const preload = () => {
    const imageSelector = window.matchMedia('(min-width: 768px)').matches
      ? '.media__image--desktop'
      : '.media__image--mobile';

    section.querySelectorAll(imageSelector).forEach(preloadResponsiveImage);
  };

  if (!('IntersectionObserver' in window)) {
    preload();
    return;
  }

  const observer = new IntersectionObserver(
    (entries) => {
      if (!entries.some((entry) => entry.isIntersecting)) return;

      preload();
      observer.disconnect();
    },
    { rootMargin: '600px 0px' },
  );
  observer.observe(section);
}

if (!customElements.get('c1-feature-tabs')) {
  customElements.define('c1-feature-tabs', C1FeatureTabs);
}

setupColorwaysImagePreloading();

document.addEventListener('shopify:section:load', (event) => {
  setupColorwaysImagePreloading(event.target);
});
